import React, {memo, ReactNode, useEffect, useRef, useState} from 'react';
import './styles.css'
import {AllActivityTypesArray} from "../../types-enums/activity";
import SelectableActivity from "../selectable-activity/SelectableActivity";
import {Divider, Fieldset, Text} from "atmosfair-ui";
import {AgencyProps} from "../../api/model/agency";
import {theme} from "../../styles/theme";
import {cloneDeep, orderBy} from "lodash";
import {CreateBundleProps} from "../../api/model/bundle";
import {Customer, CustomerCompany} from "../../api/model/customer";
import JourneyDnDContainer, {
    DnDContainerProps,
    JOURNEY_DND_CONTAINER_WHICH_VIEW
} from "../../container/journey/journey-dnd-container/JourneyDnDContainer";
import {CreateJourneyProps} from "../../redux/reducer/journey";
import {DateTime} from "luxon";
import {useEffectAllDepsChange} from '../../hooks/custom-use-effect';
import {mergeTravelComponentsToJourneyElements} from "../../utils/converter/travel-components";
import {toast} from "react-toastify";
import ToastWithTextOnly from "../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {DurationProps} from "../../types-enums/activity-common-properties";
import CustomerForm from '../forms/customer-form/CustomerForm';
import {checkForEmptyCustomer} from "../../utils/check/common";
import NameDescriptionAgencyForm, {
    ValidNameDescriptionAgencyFormDataProps
} from "../forms/name-description-agency-form/NameDescriptionAgencyForm";
import PersonCountDateForm, {ValidPersonCountDateFormProps} from "../forms/person-count-date-form/PersonCountDateForm";
import {getDurationInDaysOrNights} from "../../utils/getter/common";
import {toString} from "../../utils/string/common";
import {activityHasReturnOption} from "../../utils/check/activities";

export interface JourneyDatesProps {
    startDate: DateTime,
    endDate: DateTime
}

export interface SaveTravelComponentsDialogueProps {
    activities: AllActivityTypesArray,
    bundleHasBeenLoaded?: boolean,
    agencies: AgencyProps[],
    validateForm: number,
    onFormIsValid: (data: CreateBundleProps | CreateJourneyProps) => void,
    createJourneyFromTravelComponents: boolean,
    persistJourneyInBackend: boolean,
    persistTravelComponentsInBackend: boolean,
    hideTravelComponents: boolean,
    journeyDurationFromSiteTitle?: DurationProps | null,
    personCountFromSiteTitle?: number | null,
    showCompany: boolean,
    defaultValues: {
        name: string | number,
        description: string,
        customer: Customer
        personCount: number | null,
        journeyDuration: DurationProps | null,
        agency: AgencyProps | null
    } | null,
}

/*
This is either the Save Travel Component dialogue (isJourney: false) to permantly store Travel Components for a given user and agency
in the database or the dialogue to create a journey (isJourney: true). It holds various Autocompletes which search the database for customers
by firstname, lastname, ... Once a customer from the result list is selected, the form autofills with the information available in the back end.
If you select a customer from the list, and edit any information that is within the Autocompletefields (first name, last name, email, company),
you do not update the customer, but when saving the Travel Compomentns, you create a new customer. If you select a customer from the list and edit
information like street, house number, ..., this in turn will not create a new customer in the database, but update the existing one. If you freely
type a customers first name, last name, ... and other information, without selecting something from the result list, this will create a new customer.
This is both true for isJourney true and false.
*/

const SaveBundleCreateJourneyDialogue = memo(function SaveBundleCreateJourneyDialogue({
                                                                                          activities,
                                                                                          agencies,
                                                                                          validateForm,
                                                                                          onFormIsValid,
                                                                                          createJourneyFromTravelComponents,
                                                                                          hideTravelComponents,
                                                                                          showCompany,
                                                                                          defaultValues,
                                                                                          persistJourneyInBackend,
                                                                                          persistTravelComponentsInBackend,
                                                                                          personCountFromSiteTitle,
                                                                                          journeyDurationFromSiteTitle,
                                                                                          bundleHasBeenLoaded,
                                                                                      }: SaveTravelComponentsDialogueProps) {
    const [personCountDateFormData, setPersonCountDateFormData] = useState<ValidPersonCountDateFormProps | null>(null)
    const [agency, setAgency] = useState<AgencyProps | null>(defaultValues?.agency ?? null)
    const [calendarOpen, setCalendarOpen] = useState(false)
    const [customerFormData, setCustomerFormData] = useState<Customer | null>(null)
    const [nameDescriptionAgencyFormData, setNameDescriptionAgencyFormData] = useState<ValidNameDescriptionAgencyFormDataProps | null>(null)
    const [selectedActivity, setSelectedActivity] = useState(activities.map(Boolean))
    const [journeyDayItems, setJourneyDayItems] = useState<AllActivityTypesArray>([])
    const [journeyDuration, setJourneyDuration] = useState<DnDContainerProps["journeyDuration"] | null>(null)
    const orderedActivities = useRef(orderBy(activities, activity => activity.common.activityType, ['desc']))

    useEffect(() => {
        if (persistJourneyInBackend && persistTravelComponentsInBackend) throw new Error('You cannot set saveJourney and saveTravelComponents to true')
        if ((persistJourneyInBackend || persistTravelComponentsInBackend) && createJourneyFromTravelComponents) throw new Error('You cannot set saveJourney and/or saveTravelComponents to true and createJourneyFromTravelComponents to true')
    }, [])

    useEffectAllDepsChange(() => {
        if (createJourneyFromTravelComponents) {
            _createJourneyFromTravelComponents()
        } else {
            return () => {
            }
        }
    }, [validateForm, customerFormData, nameDescriptionAgencyFormData, personCountDateFormData])

    useEffectAllDepsChange(() => {
        if (persistJourneyInBackend || persistTravelComponentsInBackend) {
            if (
                (customerFormData !== null && Object.values(customerFormData).some(value => value && value.length > 1)) ||
                (customerFormData !== null && Object.values(customerFormData).some(value => value && value.length > 1))
            ) {
                const {firstName, lastName, email, company} = customerFormData
                _saveBundleOrShowToast(email, firstName, lastName, company)
            } else {
                _saveBundle()
            }
        } else {
            return () => {
            }
        }
    }, [validateForm, customerFormData, nameDescriptionAgencyFormData])

    const _saveBundleOrShowToast = (firstName: string, lastName: string, email: string, company: CustomerCompany | null) => {
        if ([email, firstName, lastName, company?.name ?? ""].some(value => value.length > 1)) {
            _saveBundle()
        } else {
            toast(<ToastWithTextOnly
                    message={"TOASTS.WARNING.MISSING_CUSTOMER"}/>,
                {progress: undefined, type: "warning"}
            )
        }
    }

    const _setCommonPropsForCreateJourneyAndSaveBundle = () => {
        if (nameDescriptionAgencyFormData) {
            const {name, description, agency} = nameDescriptionAgencyFormData
            if (agency !== null) return {
                name: toString(name),
                description: description.length === 0 ? null : description,
                agency,
                customer: checkForEmptyCustomer(customerFormData),
            }
        }
    }

    const _createJourneyFromTravelComponents = () => {
        if (journeyDuration) {
            const data = _setCommonPropsForCreateJourneyAndSaveBundle()
            const journeyDaysCount = getDurationInDaysOrNights(journeyDuration, false)
            if (data && journeyDayItems && journeyDaysCount !== null) {
                let personCount = null
                let journeyDuration = null
                if (personCountDateFormData) {
                    personCount = personCountDateFormData.personCount
                    journeyDuration = personCountDateFormData.journeyDuration
                    if (journeyDuration !== null) onFormIsValid({
                        ...data,
                        personCount,
                        journeyDuration,
                        activities: mergeTravelComponentsToJourneyElements(journeyDayItems, journeyDaysCount, true, true)
                    })
                }

            }
        }
    }

    const _saveBundle = () => {
        const data = _setCommonPropsForCreateJourneyAndSaveBundle()
        if (data) {
            let personCount = null
            let journeyDuration = null
            if (
                persistJourneyInBackend &&
                journeyDurationFromSiteTitle &&
                personCountFromSiteTitle !== null &&
                typeof personCountFromSiteTitle !== 'undefined'
            ) {
                personCount = personCountFromSiteTitle
                journeyDuration = journeyDurationFromSiteTitle
            }
            onFormIsValid({
                ...data,
                personCount,
                journeyDuration,
                activities: orderedActivities.current.filter((_, index) => selectedActivity[index])
            })
        }
    }

    const _renderActivities = () => {
        let activitiesWithReturn: Set<string> = new Set()
        orderedActivities.current.forEach(activity => {
            if (activityHasReturnOption(activity) && activity.returnId) activitiesWithReturn.add(activity.returnId)
        })
        const activitiesToRender: ReactNode[] = []
        orderedActivities.current.forEach((activity, index) => {
            if (!activitiesWithReturn.has(activity.common.id)) {
                activitiesToRender.push(
                    <SelectableActivity
                        selectTravelComponent={() => {
                            const updatedSelectedTravelComponents = cloneDeep(selectedActivity)
                            updatedSelectedTravelComponents[index] = !updatedSelectedTravelComponents[index]
                            setSelectedActivity(updatedSelectedTravelComponents)
                        }}
                        showReturnIcon={true}
                        key={index}
                        selected={selectedActivity[index]}
                        activity={activity}/>
                )
            }

        })
        return activitiesToRender
    }
    
    return <Fieldset
        showLegend={false}
        legend={`LABELS.${createJourneyFromTravelComponents ? 'JOURNEY' : 'TRAVEL_COMPONENTS'}.GENERAL_INFORMATION_ON_FORM`}
        fieldsetClasses={'save-bundle-dialogue-container'}>
        <Fieldset
            legend={`LABELS.${createJourneyFromTravelComponents ? 'JOURNEY' : 'TRAVEL_COMPONENTS'}.TRAVEL_COMPONENTS_INFORMATION`}
            showLegend={false}
            fieldsetClasses={'form-row'}>
            <NameDescriptionAgencyForm
                hideAgencySelection={Boolean(bundleHasBeenLoaded)}
                setAgencySelection={setAgency}
                agencies={agencies}
                defaultValues={defaultValues ? {
                    name: defaultValues.name,
                    description: defaultValues.description,
                    agency: defaultValues.agency
                } : null}
                validateForm={validateForm}
                onFormIsValid={setNameDescriptionAgencyFormData}
                nameLabel={createJourneyFromTravelComponents || persistJourneyInBackend ? "GENERAL.JOURNEY_NAME" : "GENERAL.TRAVEL_COMPONENT_NAME"}
            />
            {createJourneyFromTravelComponents &&
                <div className={"form-row"}>
                    <PersonCountDateForm
                        setSelectedJourneyDuration={setJourneyDuration}
                        onFormIsValid={setPersonCountDateFormData}
                        setCalendarOpen={setCalendarOpen}
                        validateForm={validateForm}
                        defaultValues={{
                            journeyDuration: defaultValues?.journeyDuration ?? null,
                            personCount: defaultValues?.personCount ?? null
                        }}/>
                </div>}
        </Fieldset>
        <Text style={{marginTop: createJourneyFromTravelComponents ? 2 * theme.spacer.large : theme.spacer.large}}
              variant={"headlineSmall"}>GENERAL.CUSTOMER</Text>
        <CustomerForm
            agencies={agencies}
            selectedAgency={agency}
            defaultCustomer={defaultValues?.customer ?? null}
            validateForm={validateForm}
            showCompany={showCompany}
            hideAgencySelection={true}
            onFormIsValid={setCustomerFormData}/>
        {!hideTravelComponents &&
            <>
                <Divider
                    margin={"large"}
                    orientation={"horizontal"}/>
                {
                    createJourneyFromTravelComponents && journeyDuration ?
                        <JourneyDnDContainer
                            whichView={JOURNEY_DND_CONTAINER_WHICH_VIEW.createFromTravelComponents}
                            journeyDuration={journeyDuration}
                            validateForm={validateForm}
                            onFormIsValid={setJourneyDayItems}
                            activities={activities}
                            allowCalculation={!calendarOpen}
                            journeyPersonCount={(typeof personCountFromSiteTitle !== 'undefined' && personCountFromSiteTitle !== null) ? personCountFromSiteTitle : 1}
                        />
                        :
                        <>
                            <Text
                                style={{marginBottom: theme.spacer.large}}
                                variant={"headlineSmall"}>MODALS.SAVE_BUNDLES.TRAVEL_COMPONENTS_TO_SAVE</Text>
                            {_renderActivities()}
                        </>
                }
            </>
        }
    </Fieldset>
})

export default SaveBundleCreateJourneyDialogue